import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GanttChartSquare, Loader2, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md stagger-in">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
            <GanttChartSquare className="h-6 w-6 text-white" />
          </div>
          <span className="font-heading font-bold text-xl">NexusLMS</span>
        </div>
        {sent ? (
          <div className="text-center border border-border rounded-xl p-8 bg-card">
            <div className="h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center mx-auto mb-4">
              <MailCheck className="h-6 w-6 text-emerald-600" />
            </div>
            <h2 className="font-heading text-xl font-bold">Check your inbox</h2>
            <p className="text-sm text-muted-foreground mt-2">If that email is registered, a reset link has been sent. It expires in 1 hour.</p>
            <Link to="/login"><Button variant="outline" className="mt-6 w-full" data-testid="back-to-login-button"><ArrowLeft className="h-4 w-4 mr-2" />Back to sign in</Button></Link>
          </div>
        ) : (
          <div className="border border-border rounded-xl p-8 bg-card">
            <h2 className="font-heading text-2xl font-bold tracking-tight">Reset your password</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-6">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" className="mt-1.5" data-testid="forgot-email-input" />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mt-6">
              <ArrowLeft className="h-4 w-4" />Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
